import { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  CheckCircle,
  Shield,
  Star,
  ArrowRight,
  Calendar,
  Wrench,  // Service icon for garbage disposals
  ChevronLeft,
  ChevronRight,
  MapPin,
  Zap
} from 'lucide-react';
import { SchemaInjector } from '../components/SchemaInjector';

const GarbageDisposalPricing = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  // Real Garbage Disposal Installation Projects
  // TODO: Replace with actual garbage disposal project photos when available
  const disposalProjects = [
    {
      id: 1,
      image: '/colorado-plumbing-uploads/existing-projects/best-plumbing-company-highlands-ranch.png',
      location: 'Highlands Ranch, CO',
      type: '1/2 HP Disposal (Moen or Similar)',
      description: 'Professional garbage disposal installation in modern kitchen',
      features: ['Standard electrical hookup', 'Leak tested']
    },
    {
      id: 2,
      image: '/colorado-plumbing-uploads/existing-projects/plumbing-truck-residential-best-plumber-castle-rock.png',
      location: 'Castle Rock, CO',
      type: '3/4 HP Disposal (Moen or Similar)',
      description: 'High-performance disposal installation for busy kitchen',
      features: ['Quality motor', 'Hardwired connection']
    },
    {
      id: 3,
      image: '/colorado-plumbing-uploads/existing-projects/trustworthy-plumbers-water-heater-littleton.png',
      location: 'Littleton, CO',
      type: '1/3 HP Disposal (Moen or Similar)',
      description: 'Budget-friendly disposal replacement with quality installation',
      features: ['Existing wiring used', 'Quick installation']
    },
    {
      id: 4,
      image: '/colorado-plumbing-uploads/existing-projects/gas-pipe-install-lakewood-colorado-first-plumbing.png',
      location: 'Lakewood, CO',
      type: 'Customer-Supplied Installation',
      description: 'Professional installation of customer-provided disposal',
      features: ['Customer disposal', 'Expert installation', 'Full testing']
    }
  ];

  // Unified Schema for the entire page
  const completePageSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://colorado-plumbing.com/garbage-disposal-pricing#webpage",
        "url": "https://colorado-plumbing.com/garbage-disposal-pricing",
        "name": "Denver Garbage Disposal Cost: $425 Installed | Colorado First Plumbing",
        "description": "See exact, flat-rate prices for garbage disposal installation in Denver. 1/3 HP: $425. 1/2 HP: $495. 3/4 HP: $775. 360 Google Reviews. No hidden fees.",
        "isPartOf": { "@id": "https://colorado-plumbing.com/#website" },
        "breadcrumb": { "@id": "https://colorado-plumbing.com/garbage-disposal-pricing#breadcrumb" }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://colorado-plumbing.com/garbage-disposal-pricing#breadcrumb",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://colorado-plumbing.com/" },
          { "@type": "ListItem", "position": 2, "name": "Garbage Disposal Pricing", "item": "https://colorado-plumbing.com/garbage-disposal-pricing" }
        ]
      },
      {
        "@type": "Plumber",
        "@id": "https://colorado-plumbing.com/",
        "name": "Colorado First Plumbing and Sewer LLC",
        "image": "https://colorado-plumbing.com/colorado-plumbing-uploads/brand/Colorado-First-Plumbing-Logo-Transparent.png",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "5108 Sydney Ave", "addressLocality": "Littleton", "addressRegion": "CO", "postalCode": "80130", "addressCountry": "US"
        },
        "url": "https://colorado-plumbing.com/garbage-disposal-pricing",
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" },
        "areaServed": [
          { "@type": "City", "name": "Denver" },
          { "@type": "AdministrativeArea", "name": "Denver Metro" }
        ]
      },
      {
        "@type": "Product", "name": "1/3 HP Garbage Disposal Installation",
        "description": "Our $425 flat-rate service includes a 1/3 HP Moen (or similar) garbage disposal and professional installation. This price covers removing the old unit and all standard plumbing connections.",
        "sku": "GD-INSTALL-1-3HP", "brand": {"@type": "Brand", "name": "Moen"},
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/garbage-disposal-pricing#garbage-disposal-pricing-details", "priceCurrency": "USD", "price": "425", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "Product", "name": "1/2 HP Garbage Disposal Installation",
        "description": "Our most popular option. The $495 flat-rate service includes a 1/2 HP Moen (or similar) garbage disposal and professional installation, including removal of the old unit.",
        "sku": "GD-INSTALL-1-2HP", "brand": {"@type": "Brand", "name": "Moen"},
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/garbage-disposal-pricing#garbage-disposal-pricing-details", "priceCurrency": "USD", "price": "495", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "Product", "name": "3/4 HP Garbage Disposal Installation",
        "description": "For heavy-duty use. Our $775 flat-rate service includes a powerful 3/4 HP Moen (or similar) garbage disposal and professional installation for maximum performance.",
        "sku": "GD-INSTALL-3-4HP", "brand": {"@type": "Brand", "name": "Moen"},
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/garbage-disposal-pricing#garbage-disposal-pricing-details", "priceCurrency": "USD", "price": "775", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "Product", "name": "Customer-Supplied Garbage Disposal Installation",
        "description": "If you've already purchased a garbage disposal, our licensed plumbers will professionally install it for a flat-rate labor fee of $225. This includes standard plumbing connections and testing.",
        "sku": "GD-INSTALL-LABOR-ONLY",
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/garbage-disposal-pricing#garbage-disposal-pricing-details", "priceCurrency": "USD", "price": "225", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "Product", "name": "Hardwired Garbage Disposal Replacement (1/2 HP)",
        "description": "Our $575 flat-rate service for hardwired systems includes a 1/2 HP Badger garbage disposal and professional installation with direct electrical connection.",
        "sku": "GD-HARDWIRED-1-2HP", "brand": {"@type": "Brand", "name": "Badger"},
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/garbage-disposal-pricing#garbage-disposal-pricing-details", "priceCurrency": "USD", "price": "575", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "Product", "name": "Air Switch Installation (Labor Only)",
        "description": "For $225, our plumbers will professionally install a customer-provided air switch for your garbage disposal, providing a safe and convenient countertop activation button.",
        "sku": "GD-AIRSWITCH-LABOR",
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/garbage-disposal-pricing#garbage-disposal-pricing-details", "priceCurrency": "USD", "price": "225", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "Product", "name": "Sink Flange / Basket Strainer Replacement",
        "description": "If your sink flange or basket strainer is leaking or damaged, we will replace it with new, professional-grade hardware for a flat-rate of $225.",
        "sku": "GD-FLANGE-REPLACE",
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/garbage-disposal-pricing#garbage-disposal-pricing-details", "priceCurrency": "USD", "price": "225", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question", "name": "How much does garbage disposal installation cost in Denver?",
            "acceptedAnswer": { "@type": "Answer", "text": "Colorado First Plumbing offers flat-rate pricing for garbage disposal installation in the Denver area. A 1/3 HP model is $425, a 1/2 HP is $495, and a 3/4 HP is $775, all including the unit and labor. If you provide your own disposal, the installation labor is a flat $225." }
          },
          {
            "@type": "Question", "name": "What is included in the standard installation price?",
            "acceptedAnswer": { "@type": "Answer", "text": "Our standard installation price includes the removal and disposal of your old unit, the new garbage disposal itself (e.g., Moen or similar), standard plumbing and electrical connections, and full functionality and leak testing." }
          },
          {
            "@type": "Question", "name": "What if my electrical wiring needs to be changed for a hardwired disposal?",
            "acceptedAnswer": { "@type": "Answer", "text": "Our specialty service for a hardwired 1/2 HP Badger disposal is $575. This covers the direct electrical connection. However, if a completely new electrical circuit is needed, that would be an additional cost and would be quoted upfront." }
          },
          {
            "@type": "Question", "name": "How do I choose the right size garbage disposal?",
            "acceptedAnswer": { "@type": "Answer", "text": "A 1/3 HP is suitable for small households with light use. A 1/2 HP is the most common size, ideal for most families. A 3/4 HP disposal is best for avid home cooks or those who handle a lot of tough food waste. Our plumbers can help you choose during your service call." }
          }
        ]
      }
    ]
  };

  useEffect(() => {
    // Auto-advance hero gallery every 5 seconds
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % disposalProjects.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [disposalProjects.length]);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % disposalProjects.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + disposalProjects.length) % disposalProjects.length);
  };

  const openLightbox = (index: number) => {
    setSelectedImage(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 to-blue-950">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-48 pb-16 bg-gradient-to-br from-blue-800 via-blue-700 to-blue-600">
        <div className="container mx-auto px-4 text-center relative z-10 py-16 md:py-24">
          <div className="inline-block px-6 py-3 mb-8 rounded-full bg-success-primary/90 text-white font-bold text-lg">
            🔧 Same Day Installation • Quality Products
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold mb-6 text-neutral-white leading-tight">
            Denver Garbage Disposal Installation Cost
            <span className="block text-2xl md:text-3xl text-blue-200 font-semibold mt-2">
              See Exact, Flat-Rate Prices Online
            </span>
          </h1>
          
          <p className="text-lg text-blue-100 mb-8">
            Master Plumber Owned • 360 Google Reviews • Professional Installation with Photo Proof
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            
            <a 
              href="#garbage-disposal-pricing-details" 
              className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-10 py-5 rounded-full text-xl font-semibold inline-block transition-all duration-300"
            >
              💰 See All Pricing Below
            </a>
          </div>
        </div>
      </section>

      {/* Trust & Authority Section */}
      <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
            Why Denver Metro Trusts Our Garbage Disposal Installation Services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
              <div className="text-4xl font-bold text-yellow-400 mb-2">18,000+</div>
              <div className="text-lg font-semibold text-white mb-2">Jobs Completed</div>
              <p className="text-blue-100 text-sm">Since 2018 serving Littleton, Denver Metro, and surrounding areas</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
              <div className="text-4xl font-bold text-green-400 mb-2">360</div>
              <div className="text-lg font-semibold text-white mb-2">Google Reviews</div>
              <p className="text-blue-100 text-sm">4.8★ rating with verified customer feedback</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
              <div className="text-4xl font-bold text-blue-400 mb-2">30+</div>
              <div className="text-lg font-semibold text-white mb-2">Jurisdictions</div>
              <p className="text-blue-100 text-sm">Licensed in 30+ Colorado cities and counties</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
              <div className="text-4xl font-bold text-green-400 mb-2">A+</div>
              <div className="text-lg font-semibold text-white mb-2">BBB Rating</div>
              <p className="text-blue-100 text-sm">Accredited business with excellent track record</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
              <div className="text-4xl font-bold text-orange-400 mb-2">ONLY</div>
              <div className="text-lg font-semibold text-white mb-2">Licensed Plumber</div>
              <p className="text-blue-100 text-sm">Showing Exact Prices Online with 360 Google Reviews</p>
            </div>
          </div>
        </div>
      </section>

      {/* Proof of Work Gallery Section */}
      <section className="py-16 bg-gradient-to-br from-neutral-white to-trust-light/30">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto mb-12">
            <div className="inline-block px-6 py-3 mb-6 rounded-full bg-success-primary text-white font-bold text-lg">
              🏆 Real Projects • Real Results • Real Customers
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mb-6 text-authority-navy">
              See Our Professional Garbage Disposal Installations
            </h2>
            <p className="text-xl text-neutral-gray mb-4">
              Colorado First Plumbing disposal installations across Denver Metro • Professional documentation • Expert craftsmanship
            </p>
          </div>

          {/* Hero Gallery Carousel */}
          <div className="relative mb-12 max-w-5xl mx-auto">
            <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl border-4 border-trust-primary/20">
              <img
                src={disposalProjects[currentImageIndex].image}
                alt={`${disposalProjects[currentImageIndex].type} installation in ${disposalProjects[currentImageIndex].location} - Colorado First Plumbing`}
                className="w-full h-full object-cover transition-all duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-authority-navy/80 via-transparent to-transparent"></div>
              
              {/* Project Info Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between">
                  <div className="mb-4 md:mb-0">
                    <div className="flex items-center mb-2">
                      <MapPin size={20} className="text-success-eco mr-2" />
                      <span className="text-success-light font-semibold">{disposalProjects[currentImageIndex].location}</span>
                    </div>
                    <h3 className="text-2xl md:text-3xl font-bold mb-2">
                      {disposalProjects[currentImageIndex].type}
                    </h3>
                    <p className="text-lg text-trust-light mb-3">
                      {disposalProjects[currentImageIndex].description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {disposalProjects[currentImageIndex].features.map((feature, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-success-primary/90 rounded-full text-sm font-medium"
                        >
                          ✓ {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-success-eco mb-1">
                      {currentImageIndex + 1} / {disposalProjects.length}
                    </div>
                    <div className="text-sm text-trust-light">Professional Projects</div>
                  </div>
                </div>
              </div>

              {/* Navigation Arrows */}
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-authority-navy p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 group"
                aria-label="Previous image"
              >
                <ChevronLeft size={24} className="group-hover:scale-110 transition-transform" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-authority-navy p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 group"
                aria-label="Next image"
              >
                <ChevronRight size={24} className="group-hover:scale-110 transition-transform" />
              </button>
            </div>

            {/* Dots Navigation */}
            <div className="flex justify-center mt-6 space-x-2">
              {disposalProjects.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    index === currentImageIndex
                      ? 'bg-success-primary scale-125'
                      : 'bg-neutral-border hover:bg-trust-primary'
                  }`}
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Project Grid Gallery */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-12">
            {disposalProjects.map((project, index) => (
              <div
                key={project.id}
                className={`group cursor-pointer rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border-2 ${
                  index === currentImageIndex 
                    ? 'border-success-primary ring-4 ring-success-primary/30 scale-105' 
                    : 'border-neutral-border hover:border-trust-primary'
                }`}
                onClick={() => {
                  setCurrentImageIndex(index);
                  openLightbox(index);
                }}
              >
                <div className="aspect-square relative overflow-hidden">
                  <img
                    src={project.image}
                    alt={`${project.type} installation in ${project.location} - Colorado First Plumbing`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-authority-navy/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-3 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <div className="flex items-center mb-1">
                      <MapPin size={14} className="text-success-eco mr-1" />
                      <span className="text-xs font-semibold">{project.location}</span>
                    </div>
                    <h4 className="text-sm font-bold leading-tight">{project.type}</h4>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <div className="bg-gradient-to-r from-success-primary to-success-eco rounded-2xl p-8 shadow-xl border border-success-primary/20 max-w-4xl mx-auto">
              <h3 className="text-2xl md:text-3xl font-bold mb-4 text-white">
                Ready to Join Our Satisfied Customers?
              </h3>
              <p className="text-lg mb-6 text-success-light">
                See your exact disposal installation cost below • Professional installation guaranteed • Same day service available
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                
                <a
                  href="#garbage-disposal-pricing-details"
                  className="bg-gradient-to-r from-trust-primary to-trust-deep hover:shadow-xl hover:scale-[1.02] text-white px-8 py-4 rounded-full text-lg font-semibold inline-block transition-all duration-300"
                >
                  💰 See Pricing Below
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Pricing Section */}
      <section id="garbage-disposal-pricing-details" className="py-20 bg-neutral-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-authority-navy">
              Transparent Garbage Disposal Pricing
            </h2>
            <p className="text-xl text-neutral-gray mb-4">
              See actual disposal installation costs with no hidden fees or surprise charges
            </p>
            <div className="inline-flex items-center bg-success-light px-6 py-3 rounded-full border-2 border-success-primary/20">
              <Shield className="w-6 h-6 text-success-primary mr-3" />
              <span className="text-success-primary font-bold">25+ Disposal Services • All Pricing Online</span>
            </div>
          </div>

          {/* Garbage Disposal Replacements */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-trust-light to-success-light/30 rounded-2xl p-8 border-2 border-trust-primary/20 mb-8">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-trust-primary to-trust-deep rounded-full flex items-center justify-center mr-6 shadow-lg">
                  <Wrench size={32} className="text-neutral-white" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-authority-navy mb-2">Garbage Disposal Replacements</h3>
                                  </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Pricing Table */}
                <div>
                  <div className="bg-neutral-white rounded-xl shadow-lg border border-trust-primary/10 overflow-hidden">
                    <div className="bg-gradient-to-r from-trust-primary to-trust-deep text-white p-4">
                      <h4 className="text-xl font-bold text-center">Disposal Replacement Pricing</h4>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                        <span className="font-semibold text-authority-navy">1/3 HP Disposal (Moen or Similar)</span>
                        <span className="text-xl font-bold text-success-primary">$425</span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                        <span className="font-semibold text-authority-navy">1/2 HP Disposal (Moen or Similar)</span>
                        <span className="text-xl font-bold text-success-primary">$495</span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                        <span className="font-semibold text-authority-navy">3/4 HP Disposal (Moen or Similar)</span>
                        <span className="text-xl font-bold text-success-primary">$775</span>
                      </div>
                      <div className="flex justify-between items-center py-3">
                        <span className="font-semibold text-authority-navy">Customer-Supplied Installation</span>
                        <span className="text-xl font-bold text-success-primary">$225</span>
                      </div>
                    </div>
                    <div className="bg-success-light p-4">
                      <h5 className="font-bold text-success-primary mb-3">Standard Installation Includes</h5>
                      <ul className="space-y-2 text-sm text-success-primary">
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Removal & disposal of old unit
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          New disposal installation (Moen or Similar)
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Standard plumbing connections
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Functionality & leak testing
                        </li>
                                              </ul>
                      <div className="mt-3 p-2 bg-yellow-50 rounded border border-yellow-200">
                                              </div>
                    </div>
                    
                    {/* Exclusions Section */}
                    <div className="bg-yellow-50 p-4 border-t border-yellow-200">
                      <p className="text-yellow-800 font-semibold text-sm mb-2">*Standard Installation Exclusions:</p>
                      <ul className="text-yellow-700 text-xs space-y-1">
                        <li>• Electrical wiring modifications</li>
                        <li>• Sink flange replacement (if needed)</li>
                        <li>• Major drain line re-plumbing</li>
                        <li>• Repair of unrelated plumbing issues</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Photo Gallery */}
                <div>
                  <div className="bg-neutral-white rounded-xl shadow-lg border border-trust-primary/10 overflow-hidden">
                    <div className="aspect-video relative">
                      <img
                        src="/colorado-plumbing-uploads/existing-projects/best-plumbing-company-highlands-ranch.png"
                        alt="Garbage disposal installation Highlands Ranch Colorado"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-authority-navy/60 to-transparent"></div>
                      <div className="absolute bottom-4 left-4 right-4 text-white">
                        <h4 className="text-lg font-bold mb-1">
                          Standard Disposal Installation - Highlands Ranch
                        </h4>
                        <p className="text-sm text-trust-light">
                          Professional 1/2 HP disposal installation with existing electrical
                        </p>
                      </div>
                    </div>
                    <div className="p-6">
                      <h4 className="font-bold text-authority-navy mb-2">Professional Disposal Installation</h4>
                      <ul className="space-y-2 text-sm text-neutral-gray">
                        <li className="flex items-center">
                          <CheckCircle size={16} className="text-success-primary mr-2" />
                          Expert removal of old unit
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="text-success-primary mr-2" />
                          Professional mounting and sealing
                        </li>
                                              </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Specialty Services */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-action-warm/10 to-action-primary/10 rounded-2xl p-8 border-2 border-action-primary/20 mb-8">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-action-primary to-action-urgent rounded-full flex items-center justify-center mr-6 shadow-lg">
                  <Zap size={32} className="text-neutral-white" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-authority-navy mb-2">Specialty Installation Services</h3>
                  <p className="text-lg text-neutral-gray">Advanced installations and accessories</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Specialty Pricing Table */}
                <div>
                  <div className="bg-neutral-white rounded-xl shadow-lg border border-action-primary/10 overflow-hidden">
                    <div className="bg-gradient-to-r from-action-primary to-action-urgent text-white p-4">
                      <h4 className="text-xl font-bold text-center">Specialty Services Pricing</h4>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                        <span className="font-semibold text-authority-navy">Hardwired Garbage Disposal Replacement (1/2 HP Badger Provided)</span>
                        <span className="text-xl font-bold text-success-primary">$575</span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                        <span className="font-semibold text-authority-navy">Air Switch Installation - Labor Only - Material Additional</span>
                        <span className="text-xl font-bold text-success-primary">$225</span>
                      </div>
                      <div className="flex justify-between items-center py-3">
                        <span className="font-semibold text-authority-navy">Sink Flange / Garbage Disposal Basket Strainer Replacement</span>
                        <span className="text-xl font-bold text-success-primary">$225</span>
                      </div>
                    </div>
                    <div className="bg-action-warm/10 p-4">
                      <h5 className="font-bold text-action-primary mb-3">Specialty Service Advantages</h5>
                      <ul className="space-y-2 text-sm text-action-primary">
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Hardwired electrical connections
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Convenient air switch operation
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Professional mounting hardware
                        </li>
                      </ul>
                    </div>
                    
                    {/* Exclusions Section */}
                    <div className="bg-yellow-50 p-4 border-t border-yellow-200">
                      <p className="text-yellow-800 font-semibold text-sm mb-2">*Specialty Service Exclusions:</p>
                      <ul className="text-yellow-700 text-xs space-y-1">
                        <li>• New electrical circuit installation</li>
                        <li>• Granite/stone countertop drilling</li>
                        <li>• Cabinet modifications</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Specialty Photo Gallery */}
                <div>
                  <div className="bg-neutral-white rounded-xl shadow-lg border border-action-primary/10 overflow-hidden">
                    <div className="aspect-video relative">
                      <img
                        src="/colorado-plumbing-uploads/existing-projects/gas-pipe-install-lakewood-colorado-first-plumbing.png"
                        alt="Hardwired garbage disposal installation Lakewood Colorado"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-authority-navy/60 to-transparent"></div>
                      <div className="absolute bottom-4 left-4 right-4 text-white">
                        <h4 className="text-lg font-bold mb-1">
                          Hardwired Installation - Lakewood
                        </h4>
                        <p className="text-sm text-trust-light">
                          Professional hardwired disposal with air switch
                        </p>
                      </div>
                    </div>
                    <div className="p-6">
                      <h4 className="font-bold text-authority-navy mb-2">Hardwired & Air Switch Installation</h4>
                      <ul className="space-y-2 text-sm text-neutral-gray">
                        <li className="flex items-center">
                          <CheckCircle size={16} className="text-success-primary mr-2" />
                          Direct electrical connection
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="text-success-primary mr-2" />
                          Convenient countertop air switch
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="text-success-primary mr-2" />
                          Safe operation near water
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ Section for Schema Parity */}
          <section className="py-16 bg-gradient-to-br from-neutral-white to-trust-light/30">
            <div className="container mx-auto px-4 max-w-4xl">
              <h2 className="text-3xl md:text-5xl font-bold mb-12 text-authority-navy text-center">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                <div className="bg-white p-6 rounded-lg shadow-md border">
                  <h3 className="font-bold text-lg text-authority-navy">How much does garbage disposal installation cost in Denver?</h3>
                  <p className="text-neutral-gray mt-2">Colorado First Plumbing offers flat-rate pricing for garbage disposal installation in the Denver area. A 1/3 HP model is $425, a 1/2 HP is $495, and a 3/4 HP is $775, all including the unit and labor. If you provide your own disposal, the installation labor is a flat $225.</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md border">
                  <h3 className="font-bold text-lg text-authority-navy">What is included in the standard installation price?</h3>
                                  </div>
                <div className="bg-white p-6 rounded-lg shadow-md border">
                  <h3 className="font-bold text-lg text-authority-navy">What if my electrical wiring needs to be changed for a hardwired disposal?</h3>
                  <p className="text-neutral-gray mt-2">Our specialty service for a hardwired 1/2 HP Badger disposal is $575. This covers the direct electrical connection. However, if a completely new electrical circuit is needed, that would be an additional cost and would be quoted upfront.</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md border">
                  <h3 className="font-bold text-lg text-authority-navy">How do I choose the right size garbage disposal?</h3>
                  <p className="text-neutral-gray mt-2">A 1/3 HP is suitable for small households with light use. A 1/2 HP is the most common size, ideal for most families. A 3/4 HP disposal is best for avid home cooks or those who handle a lot of tough food waste. Our plumbers can help you choose during your service call.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Final CTA Section */}
          <div className="text-center">
            <div className="bg-gradient-to-r from-trust-deep to-authority-navy rounded-2xl p-8 text-white shadow-2xl border border-trust-primary/20">
              <h3 className="text-3xl font-bold mb-4">Ready for Professional Garbage Disposal Installation?</h3>
              <p className="text-xl mb-6 text-trust-light">
                See your exact price above • No hidden fees • Master plumber owned • 360 Google Reviews
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                
                <a 
                  href="/faucet-pricing" 
                  className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-10 py-5 rounded-full text-xl font-semibold inline-block transition-all duration-300 w-full sm:w-auto"
                >
                  🚿 See Faucet Pricing
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Image Lightbox Modal */}
      {selectedImage !== null && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={closeLightbox}>
          <div className="relative max-w-6xl w-full max-h-full">
            <img
              src={disposalProjects[selectedImage].image}
              alt={`${disposalProjects[selectedImage].type} installation in ${disposalProjects[selectedImage].location} - Colorado First Plumbing`}
              className="w-full h-auto max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 bg-white/90 hover:bg-white text-authority-navy p-2 rounded-full shadow-lg transition-all duration-200"
              aria-label="Close lightbox"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="absolute bottom-4 left-4 right-4 bg-black/80 rounded-lg p-4 text-white">
              <div className="flex items-center mb-2">
                <MapPin size={20} className="text-success-eco mr-2" />
                <span className="text-success-light font-semibold">{disposalProjects[selectedImage].location}</span>
              </div>
              <h3 className="text-xl font-bold mb-2">{disposalProjects[selectedImage].type}</h3>
              <p className="text-trust-light mb-3">{disposalProjects[selectedImage].description}</p>
              <div className="flex flex-wrap gap-2">
                {disposalProjects[selectedImage].features.map((feature, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-success-primary/90 rounded-full text-sm font-medium"
                  >
                    ✓ {feature}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
      
      {/* Schema Injection */}
      <SchemaInjector 
        schemas={[completePageSchema]}
        title="Denver Garbage Disposal Cost: $425 Installed | Colorado First Plumbing"
        description="See exact, flat-rate prices for garbage disposal installation in Denver. 1/3 HP: $425. 1/2 HP: $495. 3/4 HP: $775. 360 Google Reviews. No hidden fees."
        canonical="https://colorado-plumbing.com/garbage-disposal-pricing"
        keywords="denver garbage disposal cost, garbage disposal installation price, moen disposal cost, flat-rate plumber denver"
      />
    </div>
  );
};

export default GarbageDisposalPricing;